import { memo, useMemo } from 'react';
import type { ReactNode } from 'react';

import type { VisibilityState } from '@tanstack/react-table';
import { Box, ButtonBase } from '@/shared/ui';
import { ArrowRightAlt, LockOutlined } from '@/shared/ui/icons';

import type { CatalogItem } from '@/entities/catalog-item';
import { Permission } from '@/entities/permissions';
import { DateTextVariants, useDateText, usePermission } from '@/features/_shared/user';
import { formatNumberWithPrecision } from '@/shared/lib/format/formatNumberWithPrecision';
import { getDiffColor } from '@/shared/lib/format/getDiffColor';

import { COLLAPSED_ROW_HEIGHT, EXPANDED_ROW_BG } from '../../constants';
import { SymbolCheckbox } from '../SymbolCheckbox/SymbolCheckbox';
import { PARTIAL_PERIOD_COUNT, PERIOD_COLUMNS } from './periodColumns';

const LEAD_CHECKBOX_WIDTH = 35;
const LEAD_FACTSHEET_WIDTH = 50;

type PeriodDetailPanelProps = Readonly<{
  periods: CatalogItem[];
  showAll: boolean;
  onShowAll: () => void;
  isInDashboard: boolean;
  renderFactsheetButton?: (code: string, mode: 'modal' | 'newTab') => ReactNode;
  columnVisibility: VisibilityState;
  onViewInForwardCurves?: () => void;
}>;

const PeriodDetailPanel = ({
  periods,
  showAll,
  onShowAll,
  isInDashboard,
  renderFactsheetButton,
  columnVisibility,
  onViewInForwardCurves
}: PeriodDetailPanelProps) => {
  const { hasPermission } = usePermission();
  const isPermissionGranted = hasPermission(Permission.ViewCatalogDetails);
  const { formatDateString } = useDateText();

  // Mirror the parent's Display Settings: a period column shows unless the main
  // table has that column toggled off. Default-visible columns are absent from
  // the visibility map (only hidden ones are stored), so `?? true` keeps them on.
  // Identity columns are always exempt (see type).
  const visibleColumns = useMemo(
    () => PERIOD_COLUMNS.filter(col => col.alwaysVisible || (columnVisibility[col.key] ?? true)),
    [columnVisibility]
  );

  const visiblePeriods = showAll ? periods : periods.slice(0, PARTIAL_PERIOD_COUNT);
  const remaining = Math.max(0, periods.length - visiblePeriods.length);

  const renderValue = (
    period: CatalogItem,
    key: string,
    kind: string | undefined,
    restricted: boolean
  ) => {
    if ((key === 'Last' || key === 'Change') && restricted) {
      return <LockOutlined sx={{ fontSize: 16, color: 'table.infoRowText' }} />;
    }
    // CatalogItem's index signature types cell values as `unknown`; every column
    // we render holds a primitive, so narrow before stringifying (never an object).
    const value = period[key];
    let raw = '';
    if (typeof value === 'string') raw = value;
    else if (typeof value === 'number') raw = String(value);
    if (kind === 'number') return formatNumberWithPrecision(raw);
    if (kind === 'date') return formatDateString(raw, DateTextVariants.FULL);
    return raw;
  };

  return (
    <Box
      data-testid='period-detail-panel'
      sx={{ backgroundColor: EXPANDED_ROW_BG, pl: '8px' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', height: 28 }}>
        <Box sx={{ width: LEAD_CHECKBOX_WIDTH, flexShrink: 0 }} />
        <Box sx={{ width: LEAD_FACTSHEET_WIDTH, flexShrink: 0 }} />
        {visibleColumns.map(col => (
          <Box
            key={col.key}
            sx={{
              width: col.width,
              flexShrink: 0,
              display: 'flex',
              justifyContent: col.align ?? 'flex-start',
              px: '8px',
              fontWeight: 700,
              fontSize: '14px'
            }}
          >
            {col.label}
          </Box>
        ))}
      </Box>

      {visiblePeriods.map(period => {
        const restricted = !isPermissionGranted || (period.Last === '' && period.Change === '');
        return (
          <Box
            key={`${period.Code}-${period.Period}-${period.PeriodType}-${period.TimeRef}`}
            sx={{ display: 'flex', alignItems: 'center', height: COLLAPSED_ROW_HEIGHT }}
          >
            <Box
              sx={{
                width: LEAD_CHECKBOX_WIDTH,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                pl: '8px'
              }}
            >
              <SymbolCheckbox
                disabled={restricted}
                symbol={period.Symbol}
              />
            </Box>
            <Box
              sx={{
                width: LEAD_FACTSHEET_WIDTH,
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              {renderFactsheetButton?.(period.Code, isInDashboard ? 'newTab' : 'modal')}
            </Box>
            {visibleColumns.map(col => (
              <Box
                key={col.key}
                sx={{
                  width: col.width,
                  flexShrink: 0,
                  display: 'flex',
                  justifyContent: col.align ?? 'flex-start',
                  px: '8px',
                  fontSize: '14px',
                  color:
                    col.key === 'Change' && !restricted
                      ? getDiffColor(Number(period.Change))
                      : 'gx.subtext'
                }}
              >
                {renderValue(period, col.key, col.kind, restricted)}
              </Box>
            ))}
          </Box>
        );
      })}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          height: 34,
          pl: `${LEAD_CHECKBOX_WIDTH + LEAD_FACTSHEET_WIDTH + 8}px`
        }}
      >
        {!showAll && remaining > 0 && (
          <ButtonBase
            disableRipple
            sx={{ color: 'gx.subtext', fontWeight: 700, fontSize: '14px' }}
            onClick={onShowAll}
          >
            +{remaining} more periods
          </ButtonBase>
        )}
        {onViewInForwardCurves && (
          <ButtonBase
            disableRipple
            sx={{
              color: 'gx.highlight',
              fontWeight: 700,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}
            onClick={onViewInForwardCurves}
          >
            View in Forward Curves
            <ArrowRightAlt sx={{ fontSize: 16 }} />
          </ButtonBase>
        )}
      </Box>
    </Box>
  );
};

export default memo(PeriodDetailPanel);
